import React from 'react'
import { observer } from 'mobx-react'
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { formatVideoDuration, Icons } from '../../../utils/utilities'
import ScrollWrapper from '../ScrollWrapper'
import { useVideoPlayerStore } from '../../../store/video-player.store'


const TimelineScrubAnimationDemo = () => {
    return (
        <Box className="timeline-scrub-animation-demo">
            <Typography fontSize="12px" align='center' marginBottom="20px" className="timeline-scrub-animation-demo-label">Drag the question markers along the timeline to change exactly when each question appears during video playback.</Typography>
            <Box className="timeline-scrub-animation-container">
                {[10, 35, 50, 65, 90].map( ( position, index ) => (
                    <Box
                        key={`marker-${index}`}
                        className={`timeline-scrub-animation-marker ${index === 2 ? 'animated' : 'static'}`}
                        sx={{ left: `${position}%`, fontSize: "26px" }}
                    >
                        {index === 2 && Icons.default.Height}
                    </Box>
                ) )}
            </Box>
        </Box>
    )
}

const VideoPlayerTimeline = observer( ( { onTimelineMouseDown } ) => {
    const store = useVideoPlayerStore()
    const { palette, border } = useTheme()
    const canEdit = store.editModePossible
    const editModeActive = store.isEditModeActive

    // store.duration is observable and kept up to date on loadedmetadata / durationchange
    const duration = store.duration

    return (
        <Box display="flex" flexDirection="column" gap="10px" flexGrow={1} justifyContent="flex-end">
            {editModeActive && canEdit && (
                <Box
                    position="relative"
                    flexGrow={1}
                    className="events-edit-panel"
                    sx={{
                        boxShadow: '2px 2px 10px 3px rgba(0,0,0,0.3)',
                        zIndex: 100000,
                        pointerEvents: 'all',
                        margin: '10px',
                        borderRadius: '10px',
                        overflow: 'clip',
                    }}
                    display="flex"
                    flexDirection="column"
                    bgcolor={palette.background.default + 'ee'}
                    gap={1}
                >
                    <Box display="flex" borderBottom={border[1]} padding="10px" gap="10px" alignItems="center">
                        <IconButton onClick={() => store.setIsEditModeActive( false )} size="small">
                            {Icons.default.ArrowBack}
                        </IconButton>
                        <Typography variant="subtitle2">Timeline Events</Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="20px" justifyContent="center" flexGrow={1}>
                        <Box display={{ xs: "none", sm: "flex" }} mx="20px" flexDirection="column" gap={1} flexWrap="wrap" alignItems="center">
                            {store.config.editOptions.canAddTimelineEvents && (
                                <Button sx={{ borderRadius: '20px' }} variant="outlined" size="small" startIcon={<AddIcon />} onClick={store.handleAddEventClick}>
                                    Add timeline event
                                </Button>
                            )}
                            {store.config.editOptions.canScrubTimelineEvents && <TimelineScrubAnimationDemo />}
                        </Box>
                        <ScrollWrapper wrapperStyles={{ padding: '5px 20px', justifyContent: 'safe center' }}>
                            {store.editableEvents.map( ( event ) => (
                                <Box
                                    id={event.id}
                                    tabIndex={0}
                                    key={event.id}
                                    flexShrink={0}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    p={1}
                                    px={2}
                                    borderRadius="999px"
                                    bgcolor="rgba(255,255,255,0.08)"
                                    onClick={() => store.handleClickOnEventChip( event )}
                                    sx={{ ':focus': { outline: `2px solid ${palette.primary.main}`, outlineOffset: '2px' } }}
                                >
                                    <Typography component="div" maxWidth="130px" fontSize="0.75rem" noWrap>
                                        {event.displayLabel || event.label}
                                    </Typography>
                                    <Typography fontSize="0.7rem" color="text.secondary">
                                        {formatVideoDuration( event.time )}
                                    </Typography>
                                    {store.config.editOptions.canDeleteTimelineEvents && (
                                        <IconButton size="small" onClick={() => store.handleDeleteEvent( event.id )}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ) )}
                        </ScrollWrapper>
                    </Box>
                </Box>
            )}

            {!editModeActive && canEdit && <Box sx={{
                boxShadow: '2px 2px 10px 3px rgba(0,0,0,0.3)',
                p: 1,
                zIndex: 100000,
                pointerEvents: 'all',
                margin: '10px',
                borderRadius: 5,
                overflow: 'clip',
                bgcolor: palette.contentBg,
                width: "fit-content"
            }}>
                <Tooltip placement="right" title="Edit timeline events">
                    <IconButton size="small" onClick={() => store.setIsEditModeActive( true )}>{Icons.default.EditIcon}</IconButton>
                </Tooltip>
            </Box>}

            <Box flexGrow={1} ref={store.setTimelineContainerRef} className="timeline-container" onPointerDown={onTimelineMouseDown} sx={{ position: 'relative' }}>
                <Box className="timeline" sx={{ position: 'relative' }}>
                    <Box component="span" className="thumb-indicator" />
                    <Box component="span" className="buffer-loaded" />
                    {store.editableEvents.map( ( event ) => {
                        const left = `${getSafePosition( event.time, Math.max( duration, 1 ) )}%`
                        return (
                            <Tooltip key={event.id} title={store.config.editOptions.canScrubTimelineEvents ? event.displayLabel ?? event.label : event.label}>
                                <Box
                                    onPointerDown={e => store.handleEventMarkerPointerDown( e, event.id )}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left,
                                        transform: 'translateX(-50%)',
                                        width: 12,
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: editModeActive && store.config.editOptions.canScrubTimelineEvents ? 'ew-resize' : 'pointer',
                                        zIndex: 300
                                    }}
                                >
                                    <Box
                                        id={`timeline-event-marker-${event.id}`}
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: '#f5a623',
                                            border: '2px solid rgba(255,255,255,0.9)',
                                            boxShadow: '0 0 0 2px rgba(0,0,0,0.12)'
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        )
                    } )}
                </Box>
            </Box>
        </Box>
    )
} )

const getSafePosition = ( time, duration ) => {
    if ( !duration || duration <= 0 ) return 0
    return Math.min( 100, Math.max( 0, ( time / duration ) * 100 ) )
}

export default VideoPlayerTimeline